import React, { useEffect, useState, useRef } from 'react';
import { TeacherCalendar } from '../TeacherCalendar';
import { useTheme } from '../providers/ThemeProvider';
import { PlusIcon } from 'lucide-react';
import { CreateNewLessonModal } from '../CreateNewLessonModal';
import { BackdatedLessonWarningModal } from '../modals/BackdatedLessonWarningModal';
import { useLessons } from '../../src/hooks/useLessons';
import { useAuth } from '../../contexts/AuthContext';
import { toUtcISOString, validateRange } from '../../src/utils/datetime';
import { toast } from 'react-hot-toast';
import { getDefaultLessonSettings } from '../../services/teacherProfileService';
// DIAGNOSTIC LOG - Prove Calendar2 wrapper is mounting
console.log('Calendar2.tsx mounted — build marker: CAL2_2025_12_19_A');
// Default timezone
const DEFAULT_TIMEZONE = 'America/Los_Angeles';
// Helper to get color based on instrument
const getInstrumentColor = (instrument: string): string => {
  const instrumentColors: Record<string, string> = {
    Piano: '#4FC3F7',
    Guitar: '#81C784',
    Violin: '#FFD54F',
    Voice: '#F48FB1',
    Drums: '#BA68C8',
    Flute: '#4DB6AC',
    Saxophone: '#FF8A65',
    Trumpet: '#FFB74D',
    Cello: '#9575CD',
    Bass: '#7986CB'
  };
  return instrumentColors[instrument] || '#B0BEC5';
};
// Helper function to get smart default time based on current local time
const getSmartDefaultTime = (): {
  date: Date;
  time: string;
} => {
  // Get current time in America/Los_Angeles timezone
  const now = new Date();
  const losAngelesTime = new Date(now.toLocaleString('en-US', {
    timeZone: DEFAULT_TIMEZONE
  }));
  // Get hours and minutes
  let hours = losAngelesTime.getHours();
  let minutes = losAngelesTime.getMinutes();
  // Round up to nearest 5 minutes
  const remainder = minutes % 5;
  if (remainder !== 0) {
    minutes += 5 - remainder;
  }
  // Handle minute overflow
  if (minutes >= 60) {
    minutes = 0;
    hours += 1;
  }
  // If after 9 PM (21:00), default to next day 9:00 AM
  let defaultDate = new Date(losAngelesTime);
  if (hours >= 21) {
    // Next day at 9:00 AM
    defaultDate.setDate(defaultDate.getDate() + 1);
    hours = 9;
    minutes = 0;
  }
  // Handle hour overflow (midnight rollover)
  if (hours >= 24) {
    hours = 0;
    defaultDate.setDate(defaultDate.getDate() + 1);
  }
  const timeStr = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  console.log('[Calendar2] Smart default time calculated:', {
    originalTime: now.toLocaleString('en-US', {
      timeZone: DEFAULT_TIMEZONE
    }),
    roundedTime: timeStr,
    date: defaultDate.toISOString().split('T')[0]
  });
  return {
    date: defaultDate,
    time: timeStr
  };
};
export const Calendar2: React.FC = () => {
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const newLessonButtonRef = useRef<HTMLButtonElement>(null);
  // Backdated warning state
  const [showBackdatedWarning, setShowBackdatedWarning] = useState(false);
  const [pendingBackdatedDate, setPendingBackdatedDate] = useState<Date | null>(null);
  const [pendingBackdatedTime, setPendingBackdatedTime] = useState<string | null>(null);
  const [teacherDefaultSettings, setTeacherDefaultSettings] = useState<{
    duration: number;
    location: string;
    notes: string;
  } | null>(null);
  // Reset scroll position on page load/refresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  // Get current month range for useLessons hook
  const currentDate = new Date();
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const startDate = new Date(Date.UTC(year, month, 1, 0, 0, 0, 0));
  const endDate = new Date(Date.UTC(year, month + 1, 0, 23, 59, 59, 999));
  const dateRange = {
    fromISOUtc: startDate.toISOString(),
    toISOUtc: endDate.toISOString()
  };
  // Use the lessons hook to get createLesson function
  const {
    createLesson
  } = useLessons({
    teacherId: currentUser?.uid || '',
    tz: DEFAULT_TIMEZONE,
    range: dateRange
  });
  // Handler for when a lesson is successfully added
  const handleLessonAdded = (lessonData: any) => {
    console.log('[CALENDAR2] Lesson added successfully:', lessonData);
    toast.success('Lesson created successfully!');
    setIsCreateModalOpen(false);
    // Reset selected date/time
    setSelectedDate(null);
    setSelectedTime(null);
  };
  // Handler for when a time slot is clicked in week view
  const handleTimeSlotClick = (date: Date, hour: number, minute: number) => {
    console.log('[CALENDAR2] Time slot clicked:', {
      date,
      hour,
      minute
    });
    const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    setSelectedDate(date);
    setSelectedTime(timeStr);
    setIsCreateModalOpen(true);
  };
  // Handler for when a month cell is clicked
  const handleMonthCellClick = (date: Date) => {
    console.log('[CALENDAR2] Month cell clicked:', date);
    // Check if the clicked date is in the past (date-only comparison)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const clickedDate = new Date(date);
    clickedDate.setHours(0, 0, 0, 0);
    const isBackdated = clickedDate < today;
    console.log('[CALENDAR2] Date check:', {
      clickedDate: clickedDate.toISOString(),
      today: today.toISOString(),
      isBackdated
    });
    // Use smart default time since month cells don't have specific times
    const {
      date: defaultDate,
      time: defaultTime
    } = getSmartDefaultTime();
    if (isBackdated) {
      // Show warning first for past dates
      setPendingBackdatedDate(date);
      setPendingBackdatedTime(defaultTime);
      setShowBackdatedWarning(true);
    } else {
      // Open modal directly for future/today dates
      setSelectedDate(date);
      setSelectedTime(defaultTime);
      setIsCreateModalOpen(true);
    }
  };
  // Handler for backdated warning confirmation
  const handleBackdatedConfirm = () => {
    console.log('[CALENDAR2] Backdated warning confirmed');
    setShowBackdatedWarning(false);
    // Open the create modal with the pending date
    if (pendingBackdatedDate) {
      setSelectedDate(pendingBackdatedDate);
      setSelectedTime(pendingBackdatedTime);
      setIsCreateModalOpen(true);
    }
    // Clear pending state
    setPendingBackdatedDate(null);
    setPendingBackdatedTime(null);
  };
  // Handler for backdated warning cancellation
  const handleBackdatedCancel = () => {
    console.log('[CALENDAR2] Backdated warning cancelled');
    setShowBackdatedWarning(false);
    setPendingBackdatedDate(null);
    setPendingBackdatedTime(null);
  };
  // Handler for when "New Lesson" button is clicked
  const handleNewLessonClick = () => {
    console.log('[Calendar2] 🆕 New Lesson button clicked');
    console.log('[Calendar2] 📋 Current teacherDefaultSettings:', {
      settings: teacherDefaultSettings,
      duration: teacherDefaultSettings?.duration,
      hasSettings: teacherDefaultSettings !== null
    });
    // Use smart default time based on current local time
    const {
      date: defaultDate,
      time: defaultTime
    } = getSmartDefaultTime();
    setSelectedDate(defaultDate);
    setSelectedTime(defaultTime);
    setIsCreateModalOpen(true);
  };
  // Load teacher's default lesson settings
  useEffect(() => {
    const loadDefaults = async () => {
      if (!currentUser?.uid) {
        console.log('[Calendar2] No user ID, skipping defaults load');
        return;
      }
      try {
        console.log('[Calendar2] 🔍 Loading teacher default settings for:', currentUser.uid);
        const settings = await getDefaultLessonSettings(currentUser.uid);
        console.log('[Calendar2] ✅ Loaded default settings:', {
          settings,
          duration: settings?.duration,
          location: settings?.location
        });
        setTeacherDefaultSettings(settings);
      } catch (error) {
        console.error('[Calendar2] ❌ Error loading default settings:', error);
      }
    };
    loadDefaults();
  }, [currentUser]);
  return <div className="min-h-[70vh] w-full" data-id="element-3007">
      <div className="max-w-7xl mx-auto" data-id="element-3008">
        <div className="mb-6 flex justify-between items-center" data-id="element-3009">
          <div data-id="element-3010">
            <h1 className={`text-3xl font-serif font-bold ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-3011">
              Calendar
            </h1>
            <p className={`hidden md:block ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-3012">
              Manage your teaching schedule and student lessons
            </p>
          </div>
          <button className={`px-4 py-2 rounded-md border-2 transition-colors flex items-center ${isDark ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} onClick={handleNewLessonClick} ref={newLessonButtonRef} data-id="element-3013">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-3014" />
            New Lesson
          </button>
        </div>
        <TeacherCalendar onCellClick={handleMonthCellClick} onTimeSlotClick={handleTimeSlotClick} data-id="element-3015" />
        {/* Create Lesson Modal - Now using CreateNewLessonModal with drag functionality */}
        <CreateNewLessonModal isOpen={isCreateModalOpen} onClose={() => {
        setIsCreateModalOpen(false);
        setSelectedDate(null);
        setSelectedTime(null);
        setTimeout(() => {
          newLessonButtonRef.current?.focus();
        }, 0);
      }} onLessonAdded={handleLessonAdded} darkMode={isDark} initialDate={selectedDate ? selectedDate.toISOString().split('T')[0] : ''} initialTime={selectedTime || ''} teacherDefaultSettings={teacherDefaultSettings} data-id="element-3016" />
        {/* Backdated Lesson Warning Modal */}
        {pendingBackdatedDate && <BackdatedLessonWarningModal isOpen={showBackdatedWarning} onConfirm={handleBackdatedConfirm} onCancel={handleBackdatedCancel} isDark={isDark} lessonDate={pendingBackdatedDate} data-id="element-3017" />}
      </div>
    </div>;
};